import styled from "@emotion/styled";
import { Global, css, keyframes } from "@emotion/react";
import toolkitCardTexture from "src/ui/assets/images/toolkit-card-texture.webp";
import { useRef } from "react";
import { useBlocNext } from "@blac/react";
import t from "src/lib/translate";
import ToolkitBloc from "./ToolkitBloc";

const globalStyles = css`
  @property --card-skew-angle {
    syntax: "<angle>";
    inherits: true;
    initial-value: -20deg;
  }
`;

/**
 * Z-index layers (lowest to highest):
 * 1: Outline (border gradient effect)
 * 10: Background (card color fill)
 * 20: Highlights (shine/gloss effect)
 * 30: Texture (paper texture overlay)
 * 100: CardContent, CardIcon (interactive content)
 * 200: Card::after (press feedback overlay)
 */
const zIndex = {
  outline: 1,
  background: 10,
  highlights: 20,
  texture: 30,
  content: 100,
  pressOverlay: 200
} as const;

const ToolkitWrapper = styled.div`
  margin: 0 auto;
  max-width: var(--content-width);
`;

const Title = styled.h1`
  font-size: var(--title-font-size, 2.25rem);
  margin-bottom: 0.5em;
  text-align: center;
`;

const Subtitle = styled.p`
  font-size: 1rem;
  text-align: center;
  color: var(--color-gray-700, #374151);
  margin-bottom: 1.5rem;
  line-height: 1.5;
`;

const CardList = styled.div`
  --card-scale: min(1rem, calc((100vw - 3rem) / 20));
  --card-transition-duration: 0.4s;
  --card-radius: 1.125em;
  --card-inset: 0.09375em;
  --card-aspect-ratio: 1.595;
  --card-texture-size: 15.625em;
  --card-shadow-blur: 2.625em;

  font-size: var(--card-scale);
  position: relative;
  scroll-margin-top: 12rem;

  --card-skew-angle: -5deg;
  transition: --card-skew-angle var(--card-transition-duration) ease-in-out;

  &[data-stack-compact="true"] {
    --card-skew-angle: -25deg;
  }
`;

const cardOpen = keyframes`
  0% {
    transform: perspective(1000px) rotateX(var(--card-skew-angle)) rotateZ(0deg);
  }
  50% {
    transform: perspective(1000px) rotateX(calc(var(--card-skew-angle) / 2)) rotateZ(-3deg);
  }
  100% {
    transform: perspective(1000px) rotateX(0deg) rotateZ(0deg);
  }
`;

const cardClose = keyframes`
  0% {
    transform: perspective(1000px) rotateX(0deg) rotateZ(0deg);
  }
  50% {
    transform: perspective(1000px) rotateX(calc(var(--card-skew-angle) / 2)) rotateZ(4deg);
  }
  100% {
    transform: perspective(1000px) rotateX(var(--card-skew-angle)) rotateZ(0deg);
  }
`;

const Card = styled.div`
  cursor: pointer;
  font-size: var(--card-scale);
  border-radius: var(--card-radius);
  display: flex;
  aspect-ratio: var(--card-aspect-ratio);
  align-items: flex-start;
  justify-content: space-between;
  gap: 1em;
  position: absolute;
  left: 0;
  right: 0;
  overflow: hidden;
  box-shadow: 0 0 var(--card-shadow-blur) 0 rgba(0, 0, 0, 0.11);
  z-index: 0;
  transform-origin: center top;
  will-change: transform, top;
  transition: top var(--card-transition-duration) ease-in-out;
  animation: ${cardClose} var(--card-transition-duration) ease-out forwards;

  &[data-active="true"] {
    animation: ${cardOpen} var(--card-transition-duration) ease-out forwards;
  }

  &::after {
    content: "";
    position: absolute;
    inset: 0;
    background: linear-gradient(
      200deg,
      rgba(255, 255, 255, 0.6),
      rgba(255, 255, 255, 0.1)
    );
    border-radius: var(--card-radius);
    opacity: 0;
    transition: opacity 0.5s ease-out;
    z-index: ${zIndex.pressOverlay};
    pointer-events: none;
  }

  &:active::after {
    opacity: 1;
    transition: opacity 0.2s ease-out;
  }
`;

const cardLayerBase = css`
  position: absolute;
  inset: 0;
  border-radius: var(--card-radius);
  pointer-events: none;
`;

const Texture = styled.div`
  ${cardLayerBase}
  background-image: url(${toolkitCardTexture});
  background-color: #565656;
  background-size: var(--card-texture-size);
  mix-blend-mode: overlay;
  opacity: 0.73;
  z-index: ${zIndex.texture};
`;

const Highlights = styled.div`
  ${cardLayerBase}
  box-shadow:
    inset -0.3125em -0.3125em 3em rgba(255, 255, 255, 0.02),
    inset 0.625em 0.625em 0.9375em rgba(255, 255, 255, 0.1);
  background: radial-gradient(
    circle at top left,
    rgba(255, 255, 255, 0.35) 0%,
    rgba(171, 171, 171, 0) 100%
  );
  z-index: ${zIndex.highlights};
`;

const Background = styled.div`
  ${cardLayerBase}
  inset: var(--card-inset);
  background: var(--bg-color);
  z-index: ${zIndex.background};
`;

const Outline = styled.div`
  ${cardLayerBase}
  z-index: ${zIndex.outline};
  background:
    linear-gradient(
      150deg,
      rgba(255, 255, 255, 0.8) 0%,
      rgba(238, 237, 237, 0.3) 37%
    ),
    linear-gradient(0deg, var(--bg-color, red) 0%, var(--bg-color, red) 100%);
`;

const CardContent = styled.div`
  flex: 1;
  position: relative;
  z-index: ${zIndex.content};
`;

const CardTitle = styled.h3`
  font-size: 1.1em;
  font-family: var(--font-family);
  font-weight: 500;
  margin: 0;
  color: var(--color-charcoal, #000);
  display: block;
  padding: 1.2em 3.6em 0.5em 1.5em;
  line-height: 140%;
  letter-spacing: -0.02em;
`;

const CardDescription = styled.p`
  font-size: 1em;
  color: var(--Greys-Charcoal, #212121);
  margin: 0.625em 0 0;
  padding: 0 1.5em 2em 1.5em;
  font-feature-settings:
    "liga" off,
    "clig" off;
  line-height: 140%;
  letter-spacing: -0.02em;
`;

const CardIcon = styled.div`
  width: 2.2em;
  aspect-ratio: 1 / 1;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  position: absolute;
  z-index: ${zIndex.content};
  top: 1em;
  right: 1em;

  svg {
    width: 100%;
    height: 100%;
  }
`;

interface ToolkitSlideProps {
  userExperienceGroup: string | undefined;
}

const ToolkitSlide = ({ userExperienceGroup }: ToolkitSlideProps) => {
  const [state, bloc] = useBlocNext(ToolkitBloc);
  const wrapperRef = useRef(null);
  bloc.setWrapperRef(wrapperRef.current);
  bloc.setUserExperienceGroup(userExperienceGroup);

  return (
    <>
      <Global styles={globalStyles} />
      <ToolkitWrapper>
        <Title>{t("toolkit.title")}</Title>
        <Subtitle>{t("toolkit.subtitle")}</Subtitle>

        <CardList
          role="list"
          aria-label={t("toolkit.ariaLabel")}
          ref={wrapperRef}
          data-stack-compact={bloc.isStackCompact ? "true" : "false"}
        >
          {bloc.TOOLKIT_ITEMS.map((item) => (
            <Card
              key={item.titleKey}
              role="listitem"
              onClick={() => bloc.setActiveCard(item)}
              style={
                {
                  "--bg-color": item.color,
                  ...bloc.computeItemStyles(item)
                } as React.CSSProperties
              }
              data-active={state.activeCard === item ? "true" : "false"}
            >
              <Texture aria-hidden="true" />
              <Background aria-hidden="true" />
              <Highlights aria-hidden="true" />
              <Outline aria-hidden="true" />
              <CardContent>
                <CardTitle>{t(item.titleKey)}</CardTitle>
                <CardDescription>{t(item.descriptionKey)}</CardDescription>
              </CardContent>
              <CardIcon aria-hidden="true">
                <item.Icon />
              </CardIcon>
            </Card>
          ))}
        </CardList>
      </ToolkitWrapper>
    </>
  );
};

export default ToolkitSlide;
